/**
 * Availability audit: find matches where one or more players are unavailable
 * for the scheduled slot (time slot, exclusion dates, or holiday dates).
 * Used to surface conflicts and drive reschedule/swap flows.
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  getAvailabilityForPlayers,
  getUnavailabilityReason,
  availabilityDateKey,
} from './playerAvailabilityService';
import { toUtcMs } from '../utils/dateTimeUtils';

function toDateKey(dateLike) {
  return availabilityDateKey(dateLike);
}

/**
 * Get weekday (1=Mon .. 7=Sun) from a date.
 */
function getWeekday(dateLike) {
  const ms = toUtcMs(dateLike);
  if (ms == null || isNaN(ms)) return null;
  const d = new Date(ms);
  const day = d.getDay();
  return day === 0 ? 7 : day;
}

/**
 * Get participant IDs for a match (singles or doubles).
 */
function getParticipantIds(match) {
  if (match.matchType === 'doubles') {
    return [...(match.team1Players || []), ...(match.team2Players || [])];
  }
  return [match.player1Id, match.player2Id].filter(Boolean);
}

/**
 * Get slot key (weekday_time) and date key (YYYY-MM-DD) from a scheduled match.
 */
function getSlotAndDateKey(match) {
  const dateLike = match.scheduledDate ?? match.scheduledAt;
  if (!dateLike) return { slotKey: '', dateKey: '' };
  const dateKey = toDateKey(dateLike);
  const weekday = getWeekday(dateLike);
  const time = (match.scheduledTime || '18:00').toString().trim();
  const slotKey = weekday != null ? `${weekday}_${time}` : '';
  return { slotKey, dateKey };
}

/**
 * Find all scheduled/in-progress matches in a tournament where at least one
 * player is unavailable for that slot (wrong time slot, exclusion date, or holiday).
 * Only considers tournaments that have askPlayerAvailability enabled and players
 * who have submitted availability; players with no availability data are treated as available.
 *
 * @param {string} tournamentId
 * @returns {Promise<Array<{ match: object, playerIdsUnavailable: string[], reasons: Record<string, string> }>>}
 */
export async function getMatchesWithUnavailablePlayers(tournamentId) {
  const matchesSnap = await getDocs(
    query(
      collection(db, 'matches'),
      where('tournamentId', '==', tournamentId),
      where('status', 'in', ['scheduled', 'in-progress'])
    )
  );
  const matches = matchesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
  if (!tSnap.exists() || !tSnap.data().askPlayerAvailability) {
    return [];
  }

  const allPlayerIds = new Set();
  matches.forEach((m) => getParticipantIds(m).forEach((id) => allPlayerIds.add(id)));
  const availabilityByPlayer = await getAvailabilityForPlayers(tournamentId, [...allPlayerIds]);

  const results = [];
  for (const match of matches) {
    const { slotKey, dateKey } = getSlotAndDateKey(match);
    if (!slotKey && !dateKey) continue;

    const participantIds = getParticipantIds(match);
    const reasons = {};
    const playerIdsUnavailable = [];

    for (const pid of participantIds) {
      const avail = availabilityByPlayer.get(pid);
      const reason = getUnavailabilityReason(slotKey, avail, dateKey);
      if (reason) {
        reasons[pid] = reason;
        playerIdsUnavailable.push(pid);
      }
    }

    if (playerIdsUnavailable.length > 0) {
      results.push({
        match,
        playerIdsUnavailable,
        reasons,
      });
    }
  }

  return results;
}
